// Text Style Plugin
import { createColorProperty, createSimpleProperty } from './helpers.js';

export default {
    id: 'text',
    label: 'Text',
    icon: 'AlignLeft',
    properties: {
        textAlign: {
            label: 'Align',
            type: 'select',
            options: ['left', 'center', 'right', 'justify', 'start', 'end'],
            default: 'left',
            // Don't generate classes - store in variant-data to avoid conflict with text-color
            toClass: () => null,
            fromClass: () => null
        },
        textColor: {
            label: 'Color',
            type: 'color',
            default: null,
            ...createColorProperty('text', ['left', 'center', 'right', 'justify', 'start', 'end',
                'xs', 'sm', 'base', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl', '8xl', '9xl'])
        },
        textDecorationLine: {
            label: 'Decoration Line',
            type: 'select',
            options: ['underline', 'overline', 'line-through', 'no-underline'],
            default: 'no-underline',
            toClass: (value) => value || null,
            fromClass: (className) => {
                if (['underline', 'overline', 'line-through', 'no-underline'].includes(className)) {
                    return className;
                }
                return null;
            }
        },
        textDecorationColor: {
            label: 'Decoration Color',
            type: 'color',
            default: null,
            toClass: (value) => {
                if (!value) return null;
                if (value.startsWith('#') || value.startsWith('rgb') || value.startsWith('hsl')) {
                    return `decoration-[${value}]`;
                }
                return `decoration-${value}`;
            },
            fromClass: (className) => {
                const match = className.match(/^decoration-(.+)$/);
                if (match) {
                    let value = match[1];
                    if (value.startsWith('[') && value.endsWith(']')) {
                        return value.slice(1, -1);
                    }
                    // Exclude decoration style values
                    if (['solid', 'double', 'dotted', 'dashed', 'wavy'].includes(value)) return null;
                    if (['auto', 'from-font', '0', '1', '2', '4', '8'].includes(value)) return null;
                    return value;
                }
                return null;
            }
        },
        textDecorationStyle: {
            label: 'Decoration Style',
            type: 'select',
            options: ['solid', 'double', 'dotted', 'dashed', 'wavy'],
            default: 'solid',
            toClass: (value) => value ? `decoration-${value}` : null,
            fromClass: (className) => {
                const options = ['solid', 'double', 'dotted', 'dashed', 'wavy'];
                const match = className.match(/^decoration-(.+)$/);
                return (match && options.includes(match[1])) ? match[1] : null;
            }
        },
        textDecorationThickness: {
            label: 'Decoration Thickness',
            type: 'select',
            options: ['auto', 'from-font', '0', '1', '2', '4', '8'],
            default: 'auto',
            toClass: (value) => value ? `decoration-${value}` : null,
            fromClass: (className) => {
                const options = ['auto', 'from-font', '0', '1', '2', '4', '8'];
                const match = className.match(/^decoration-(.+)$/);
                // Also support arbitrary values for thickness e.g. decoration-[3px]
                if (match) {
                    if (options.includes(match[1])) return match[1];
                    if (match[1].startsWith('[') && match[1].endsWith(']')) return match[1]; // Arbitrary value
                }
                return null;
            }
        },
        textUnderlineOffset: {
            label: 'Underline Offset',
            type: 'select',
            options: ['auto', '0', '1', '2', '4', '8'],
            default: 'auto',
            ...createSimpleProperty('underline-offset')
        },
        textTransform: {
            label: 'Transform',
            type: 'select',
            options: ['uppercase', 'lowercase', 'capitalize', 'normal-case'],
            default: 'normal-case',
            toClass: (value) => value || null,
            fromClass: (className) => {
                if (['uppercase', 'lowercase', 'capitalize', 'normal-case'].includes(className)) {
                    return className;
                }
                return null;
            }
        },
        textOverflow: {
            label: 'Overflow',
            type: 'select',
            options: ['truncate', 'text-ellipsis', 'text-clip'],
            default: null,
            toClass: (value) => value || null,
            fromClass: (className) => {
                if (['truncate', 'text-ellipsis', 'text-clip'].includes(className)) {
                    return className;
                }
                return null;
            }
        },
        textWrap: {
            label: 'Wrap',
            type: 'select',
            options: ['text-wrap', 'text-nowrap', 'text-balance', 'text-pretty'],
            default: 'text-wrap',
            toClass: (value) => value || null,
            fromClass: (className) => {
                if (['text-wrap', 'text-nowrap', 'text-balance', 'text-pretty'].includes(className)) {
                    return className;
                }
                return null;
            }
        },
        textIndent: {
            label: 'Indent',
            type: 'select',
            options: ['0', 'px', '0.5', '1', '1.5', '2', '2.5', '3', '3.5', '4', '5', '6', '7', '8', '9', '10', '11', '12', '14', '16', '20', '24', '28', '32', '36', '40'],
            default: '0',
            ...createSimpleProperty('indent')
        },
        verticalAlign: {
            label: 'Vertical Align',
            type: 'select',
            options: ['baseline', 'top', 'middle', 'bottom', 'text-top', 'text-bottom', 'sub', 'super'],
            default: 'baseline',
            // Store in variant-data instead of classes
            toClass: () => null,
            fromClass: () => null
        },
        whiteSpace: {
            label: 'White Space',
            type: 'select',
            options: ['whitespace-normal', 'whitespace-nowrap', 'whitespace-pre', 'whitespace-pre-line', 'whitespace-pre-wrap', 'whitespace-break-spaces'],
            default: 'whitespace-normal',
            toClass: (value) => value || null,
            fromClass: (className) => {
                if (className.startsWith('whitespace-')) {
                    return className;
                }
                return null;
            }
        },
        wordBreak: {
            label: 'Word Break',
            type: 'select',
            options: ['break-normal', 'break-words', 'break-all', 'break-keep'],
            default: 'break-normal',
            toClass: (value) => value || null,
            fromClass: (className) => {
                if (className.startsWith('break-')) {
                    return className;
                }
                return null;
            }
        },
        overflowWrap: {
            label: 'Overflow Wrap',
            type: 'select',
            options: ['overflow-wrap-normal', 'overflow-wrap-anywhere', 'overflow-wrap-break-word'],
            default: 'overflow-wrap-normal',
            toClass: (value) => value || null,
            fromClass: (className) => {
                if (className.startsWith('overflow-wrap-')) {
                    return className;
                }
                return null;
            }
        },
        hyphens: {
            label: 'Hyphens',
            type: 'select',
            options: ['hyphens-none', 'hyphens-manual', 'hyphens-auto'],
            default: 'hyphens-none',
            toClass: (value) => value || null,
            fromClass: (className) => {
                if (className.startsWith('hyphens-')) {
                    return className;
                }
                return null;
            }
        },
        lineClamp: {
            label: 'Line Clamp',
            type: 'select',
            options: ['none', '1', '2', '3', '4', '5', '6'],
            default: 'none',
            toClass: (value) => value && value !== 'none' ? `line-clamp-${value}` : null,
            fromClass: (className) => {
                const match = className.match(/^line-clamp-(.+)$/);
                return match ? match[1] : null;
            }
        }
    }
};
